/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable no-unused-vars */
import { ErrorMessage, FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { IForgotPassword, ISecurityQuestionAnswer } from 'src/interfaces';
import closeSvg from 'src/assets/close.svg';
import { AlertAdapter, LargeButton } from 'pg-ui-library';

import * as yup from 'yup';
import forgotPasswordService from 'src/services/forgot-password.service';
import React, { useState } from 'react';
import styled from 'styled-components';

import {
  CenterText,
  CloseIcon,
  IconWrapper,
  FormTitle,
  ModalInput,
  StyledErrorWrap,
  Text,
  QuestionInput,
  InputCalendarContainer,
  CustomDateInput,
  StyledCalendarIcon,
} from '../styles';

const ForgotAnswerBox = styled.div`
  display: flex;
  gap: 30px;
  justify-content: flex-end;
  cursor: pointer;
`;

const ForgotAnswerText = styled.p`
  font: normal normal 600 12px/25px Biotif;
  color: #4a4ba9;
  margin: 0;
`;

const BackToAnswerBox = styled.div`
  display: flex;
  justify-content: center;
  cursor: pointer;
`;

const BackToAnswerText = styled.p`
  font: normal normal 600 16px/25px Biotif;
  color: #4a4ba9;
  margin: 0;
`;

export const ResetPasswordSecurityQuestionAnswerForm: React.FC<{
  handleModal: Function;
  handleModalState: Function;
  email: string;
  securityQuestionAnswer: ISecurityQuestionAnswer[];
}> = ({ handleModal, handleModalState, securityQuestionAnswer, email }) => {
  const [serverError, setServerError] = useState<string>('');
  const [forgotAnswers, setForgotAnswers] = useState(false);
  const [dob, setDob] = useState('');
  const { t } = useTranslation();

  const securityQuestionAnswerSchema = yup.object().shape({
    email: yup.string().required().email(),
    securityQuestionAnswer: yup
      .array()
      .of(
        yup.object().shape({
          answer: yup.string().min(1, t('global.form.min')).required(t('global.form.required')),
        })
      )
      .required()
      .min(2),
  });

  const onSubmit = async (
    values: IForgotPassword,
    { setSubmitting, setFieldError }: FormikHelpers<IForgotPassword>
  ) => {
    try {
      await forgotPasswordService.forgotPassword(values);
      setServerError('');
      AlertAdapter(t('login.resetPassword.email.sent'), 'info');
      handleModal();
    } catch (error: any) {
      setServerError(error.message);
    }
  };

  const onSubmitWithDOB = async () => {
    try {
      await forgotPasswordService.forgotPasswordWithDOB({ dob, email });
      setServerError('');
      AlertAdapter(t('login.resetPassword.email.sent'), 'info');
      handleModal();
    } catch (error: any) {
      setServerError(error.message);
    }
  };

  const onSubmitForExternal = async () => {
    try {
      await forgotPasswordService.forgotPasswordForExternal({ email });
      setServerError('');
      AlertAdapter(t('login.resetPassword.email.sent'), 'info');
      handleModal();
    } catch (error: any) {
      AlertAdapter(error.message, 'error');
    }
  };

  if (forgotAnswers) {
    return (
      <Form>
        <IconWrapper>
          <CloseIcon src={closeSvg} onClick={() => handleModal()} />
        </IconWrapper>
        <FormTitle>{t('login.resetPassword.title')}</FormTitle>
        <CenterText>{t('login.resetPassword.dob.title')}</CenterText>

        <Text>{t('login.resetPassword.dob.label')}</Text>
        <ModalInput>
          <InputCalendarContainer>
            <CustomDateInput
              type="date"
              onChange={e => {
                setDob(e.target.value);
              }}
              value={dob}
              id="birthDate"
              name="birthDate"
              placeholder="MM/DD/YYYY"
              required
            />
            <StyledCalendarIcon />
          </InputCalendarContainer>
        </ModalInput>

        {serverError !== '' ? (
          <StyledErrorWrap data-cy="reset-password-security-question-error">
            {t('login.resetPassword.dob.error')}
          </StyledErrorWrap>
        ) : null}
        <LargeButton data-cy="reset-password-security-question-submit" type="button" onClick={onSubmitWithDOB}>
          {t('login.resetPassword.button')}
        </LargeButton>

        <BackToAnswerBox onClick={() => setForgotAnswers(false)}>
          <BackToAnswerText>{t('login.resetPassword.securityQuestion.backToAnswers')}</BackToAnswerText>
        </BackToAnswerBox>
      </Form>
    );
  }

  return (
    <Formik
      initialValues={{
        email,
        securityQuestionAnswer,
      }}
      validationSchema={securityQuestionAnswerSchema}
      onSubmit={onSubmit}
    >
      {({ errors, handleChange, touched, isSubmitting }) => (
        <Form>
          <IconWrapper>
            <CloseIcon src={closeSvg} onClick={() => handleModal()} />
          </IconWrapper>
          <FormTitle>{t('login.resetPassword.title')}</FormTitle>
          <CenterText>{t('login.resetPassword.securityQuestion.text')}</CenterText>
          <FieldArray
            name="securityQuestionAnswer"
            render={() => (
              <>
                {securityQuestionAnswer.length <= 0 && (
                  <CenterText>{t('login.resetPassword.securityQuestion.emptyQuestions')}</CenterText>
                )}
                {securityQuestionAnswer.map((question, index) => (
                  <div key={question.questionId}>
                    <Text data-cy={`reset-password-security-question-text-${index}`}>{question.question}*</Text>
                    <ModalInput>
                      <QuestionInput
                        data-cy={`reset-password-security-question-input-${index}`}
                        type="text"
                        placeholder={t('login.resetPassword.securityQuestion.input')}
                        name={`securityQuestionAnswer[${index}].answer`}
                        onChange={handleChange}
                      />
                    </ModalInput>

                    <ErrorMessage name={`securityQuestionAnswer[${index}].answer`} component={StyledErrorWrap} />
                  </div>
                ))}
              </>
            )}
          />
          <ForgotAnswerBox>
            <ForgotAnswerText onClick={() => onSubmitForExternal()}>
              {t('login.resetPassword.securityQuestion.externalUser')}
            </ForgotAnswerText>
            <ForgotAnswerText onClick={() => setForgotAnswers(true)}>
              {t('login.resetPassword.securityQuestion.forgotAnswers')}
            </ForgotAnswerText>
          </ForgotAnswerBox>
          {serverError !== '' ? (
            <StyledErrorWrap data-cy="reset-password-security-question-error">
              {t('login.resetPassword.securityQuestion.error')}
            </StyledErrorWrap>
          ) : null}
          <LargeButton data-cy="reset-password-security-question-submit" type="submit" disabled={isSubmitting}>
            {t('login.resetPassword.button')}
          </LargeButton>
        </Form>
      )}
    </Formik>
  );
};
